import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Phone, Mail, MapPin } from "lucide-react";
import Header from "../components/site/Header";
import Seo from "../components/site/Seo";
import Footer from "../components/site/Footer";
import CTAButton from "../components/site/CTAButton";
import Photo from "../components/site/Photo";
import ShootPlanner from "../components/site/ShootPlanner";
import SamplePricing from "../components/site/SamplePricing";
import FAQSection from "../components/site/FAQSection";
import QuoteForm from "../components/site/QuoteForm";
import ScrollReveal from "../components/site/ScrollReveal";
import { SUBURB_GROUPS } from "../data/suburbs";

export default function Home() {
  return (
    <div className="min-h-screen bg-ink text-bone">
      <Seo />
      <Header />
      <Hero />
      <SamplePricing />
      <ShootPlanner />
      <WhyMenuPhotosMatter />
      <BeforeAfter />
      <PhotographyServices />
      <MelbourneFocus />
      <DeliveryAppSection />
      <FAQSection />
      <EnquiryCTA />
      <Footer />
    </div>
  );
}

// ─── 1. HERO ─────────────────────────────────────────────
const Hero = () => (
  <section className="relative min-h-[100svh] overflow-hidden bg-ink">
    <div className="absolute inset-0">
      <Photo
        slug="hero-spread-01"
        alt="Restaurant photography Melbourne — catering spread"
        priority
        ratio="auto"
        className="!relative h-full w-full"
      />
    </div>
    <div className="absolute inset-0 bg-gradient-to-b from-ink/75 via-ink/40 to-ink" />
    <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/30 to-transparent" />

    <div className="relative mx-auto max-w-[1600px] px-6 lg:px-10 min-h-[100svh] flex flex-col pt-24 sm:pt-28 lg:pt-32 pb-10 lg:pb-12">
      <div className="hidden sm:flex items-center justify-between text-bone/70 mb-8 lg:mb-12">
        <p className="font-display text-base sm:text-lg tracking-editorial leading-none" data-testid="hero-brand">
          Restaurant<span className="italic font-light text-ember"> Photographer</span>
        </p>
        <p className="hidden sm:block text-xs uppercase tracking-wider2 font-bold">
          Melbourne · VIC
        </p>
      </div>

      <div className="flex-1 flex flex-col justify-center max-w-5xl">
        <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-5 sm:mb-7 font-bold" data-testid="hero-eyebrow">
          Restaurant Photographer Melbourne · From $10 per image
        </p>

        <h1
          data-testid="hero-headline"
          className="font-display tracking-editorial leading-[0.92]"
          style={{ fontSize: "clamp(2.5rem, 8.5vw, 7.5rem)" }}
        >
          Menu photos
          <br />
          that actually{" "}
          <span className="italic font-light text-ember">sell food.</span>
        </h1>

        <p data-testid="hero-subline" className="mt-6 sm:mt-8 max-w-xl text-sm sm:text-base md:text-lg leading-relaxed text-bone/80">
          Mobile menu and food photography for Melbourne restaurants and cafés —
          shot on-location, ready for Uber Eats, Google Business, your website
          and socials. Volume pricing from $10 per image (min spend $1,000).
        </p>

        <ul data-testid="hero-trust" className="mt-6 sm:mt-8 flex flex-wrap gap-x-5 gap-y-2 text-xs sm:text-sm text-bone/75">
          {[
            "On-location, professional lighting",
            "Edited files in 5–7 days",
            "Print, web and delivery-app ready",
          ].map((t) => (
            <li key={t} className="flex items-center gap-2">
              <Check size={14} strokeWidth={1.6} className="text-ember shrink-0" />
              <span>{t}</span>
            </li>
          ))}
        </ul>

        <div className="mt-8 sm:mt-10 flex flex-wrap gap-3">
          <a href="#planner" data-testid="hero-cta-primary" className="w-full sm:w-auto">
            <CTAButton variant="primary" size="lg" className="w-full sm:w-auto">Estimate My Menu Shoot</CTAButton>
          </a>
          <a href="#enquiry" data-testid="hero-cta-secondary" className="w-full sm:w-auto">
            <CTAButton variant="ghost" size="lg" arrow={false} className="w-full sm:w-auto">Book a quick call</CTAButton>
          </a>
        </div>
      </div>

      <div className="mt-10 lg:mt-12 grid grid-cols-3 gap-4 md:gap-10 max-w-3xl border-t border-bone/15 pt-6">
        {[
          ["Min. shoot", "15 images"],
          ["Turnaround", "5–7 days"],
          ["Coverage", "All Melbourne"],
        ].map(([k, v]) => (
          <div key={k}>
            <p className="text-xs uppercase tracking-wider2 font-bold text-ember mb-1.5">{k}</p>
            <p className="font-sans font-bold text-base sm:text-lg md:text-xl leading-tight tracking-tight">{v}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── 3. WHY BETTER MENU PHOTOS MATTER ─────────────────────────────────────────────
const WhyMenuPhotosMatter = () => (
  <section id="why" className="relative bg-ink text-bone py-24 lg:py-36 overflow-hidden">
    <div className="absolute inset-0 grain opacity-50 pointer-events-none" />
    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16">
        <div className="col-span-12 lg:col-span-7">
          <ScrollReveal>
            <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
              Why this matters · For Melbourne restaurant operators
            </p>
            <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 6vw, 5rem)" }}>
              Most menus lose orders
              <br />
              <span className="italic font-light text-ember">before the food arrives.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-8 text-bone/85 leading-relaxed">
            <ScrollReveal delay={120}>
              <p className="text-lg md:text-xl leading-relaxed">
                A customer scrolls Uber Eats for fourteen seconds before deciding.
                On Google, they decide in less. The food is the same — the
                <strong className="text-bone"> photograph is what loses the order.</strong>
              </p>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <p className="text-base md:text-lg leading-relaxed">
                The menu photography Melbourne restaurants need today is not a
                portfolio shoot — it&apos;s an operational asset. A single
                on-location day produces a library of dish, drink and venue
                images that work across <strong className="text-bone">Uber Eats,
                DoorDash, Menulog, Google Business, your website and Instagram</strong>
                — all consistent in lighting, crop and colour.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={280}>
              <p className="text-base md:text-lg leading-relaxed">
                We work with restaurants, cafés and hospitality groups across
                Melbourne — from Footscray to Carlton, Point Cook to Glen
                Waverley. No studios, no shipping food, no three-day
                productions.
              </p>
            </ScrollReveal>

            {/* Trust bullets — quick proof */}
            <ScrollReveal delay={340}>
              <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {[
                  "Edited files in 5–7 business days",
                  "One free re-edit per dish, no questions",
                  "Square + landscape crops, named to match your menu",
                  "Consistent white-balance across the full menu",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3 text-base text-bone/90">
                    <Check size={16} strokeWidth={2} className="text-ember mt-1 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </ScrollReveal>

            <ScrollReveal delay={400}>
              <div className="flex flex-wrap gap-3 mt-4">
                <a href="#planner">
                  <CTAButton variant="ghost" size="md">Estimate My Menu Shoot</CTAButton>
                </a>
                <a href="tel:0494756111" className="inline-flex">
                  <CTAButton variant="primary" size="md" arrow={false}>Call 0494 756 111</CTAButton>
                </a>
              </div>
            </ScrollReveal>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-5 space-y-3 md:space-y-5 lg:mt-4">
          <ScrollReveal delay={120}>
            <Photo slug="hero-raj-kachori-08" alt="Restaurant photographer Melbourne — chaat" ratio="4/5" sizes="(min-width: 1024px) 35vw, 100vw" />
          </ScrollReveal>
          <div className="grid grid-cols-2 gap-3 md:gap-5">
            <ScrollReveal delay={180}>
              <Photo slug="hero-laddu-220" alt="Food photography Melbourne" ratio="1/1" sizes="20vw" />
            </ScrollReveal>
            <ScrollReveal delay={240}>
              <Photo slug="hero-gulab-jamun-00" alt="Cafe photography Melbourne" ratio="1/1" sizes="20vw" />
            </ScrollReveal>
          </div>
        </div>
      </div>

      {/* Stat band */}
      <div className="mt-20 lg:mt-28 grid grid-cols-1 md:grid-cols-3 gap-px bg-bone/15 border border-bone/15">
        {[
          ["14 sec", "Average time a customer spends on a delivery-app menu before deciding"],
          ["35%", "Typical lift in Uber Eats add-to-cart on dishes that get re-shot"],
          ["1 day", "All you need on-location for a 30–60 image library"],
        ].map(([n, c]) => (
          <div key={n} className="bg-ink p-8 lg:p-10">
            <p className="font-sans font-bold tracking-tight leading-none" style={{ fontSize: "clamp(2.5rem, 4vw, 3.75rem)" }}>
              {n}
            </p>
            <p className="mt-4 text-base text-bone/80 leading-relaxed max-w-xs">{c}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── 4. BEFORE / AFTER ─────────────────────────────────────────────
const BAComparison = ({ slug, beforeCaption, afterCaption }) => (
  <div className="grid grid-cols-2 gap-px bg-ink/20 border border-ink/15">
    <div className="relative aspect-[4/5] overflow-hidden">
      <Photo slug={slug} alt="Before — phone snap simulation" ratio="4/5" sizes="50vw" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backdropFilter: "saturate(0.45) brightness(0.7) blur(2.5px)",
          WebkitBackdropFilter: "saturate(0.45) brightness(0.7) blur(2.5px)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/75 via-transparent to-ink/15 pointer-events-none" />
      <div className="absolute top-4 left-4">
        <span className="bg-ink text-bone text-xs uppercase tracking-wider2 font-semibold px-2.5 py-1.5">
          Before · Phone snap
        </span>
      </div>
      <div className="absolute bottom-4 left-4">
        <p className="font-display italic text-bone text-base md:text-lg tracking-editorial">
          {beforeCaption}
        </p>
      </div>
    </div>
    <div className="relative aspect-[4/5] overflow-hidden">
      <Photo slug={slug} alt="After — on-location restaurant photography" ratio="4/5" sizes="50vw" />
      <div className="absolute top-4 left-4">
        <span className="bg-ember text-bone text-xs uppercase tracking-wider2 font-semibold px-2.5 py-1.5">
          After · On-location shoot
        </span>
      </div>
      <div className="absolute bottom-4 left-4">
        <p className="font-display italic text-bone text-base md:text-lg tracking-editorial drop-shadow-lg">
          {afterCaption}
        </p>
      </div>
    </div>
  </div>
);

const BeforeAfter = () => (
  <section id="before-after" className="bg-bone text-ink py-24 lg:py-36">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-12 mb-14 lg:mb-16 items-end">
        <div className="col-span-12 lg:col-span-6">
          <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-6 font-bold">
            Same dish · Different sale
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            What your customers see
            <br />
            <span className="italic font-light text-ember">vs. what we shoot.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-6 lg:pb-3">
          <p className="text-char text-base md:text-lg leading-relaxed mb-6">
            Most restaurant photos online are still phone snaps under
            fluorescent kitchen lights. Below: the same dish, lit and styled
            properly. The food doesn&apos;t change — the order rate does.
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2.5 text-sm md:text-base text-char">
            {[
              "Higher tap-through on Uber Eats",
              "Better add-to-cart conversion",
              "Lifts perceived menu price-point",
              "Consistent across all dishes",
            ].map((b) => (
              <li key={b} className="flex items-start gap-2.5">
                <Check size={14} strokeWidth={2} className="text-ember mt-1 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="space-y-8 md:space-y-12">
        <ScrollReveal>
          <BAComparison
            slug="hero-gulab-jamun-00"
            beforeCaption="Flat. Cold. Skipped."
            afterCaption="Hidden gulab jamun, pistachio."
          />
        </ScrollReveal>
        <ScrollReveal delay={120}>
          <BAComparison
            slug="hero-raj-kachori-08"
            beforeCaption="Lost in the menu."
            afterCaption="Raj Kachori — top of the order."
          />
        </ScrollReveal>
      </div>

      <div className="mt-16 flex flex-col sm:flex-row sm:items-center justify-between gap-6 pt-10 border-t border-ink/15">
        <p className="text-xs sm:text-sm uppercase tracking-wider2 text-char-mute max-w-xl font-medium">
          Before frames are a simulation of typical phone-on-table photography
          to illustrate the contrast. Both images are real shoots.
        </p>
        <a href="#planner">
          <CTAButton variant="secondary" size="md">Estimate My Menu Shoot</CTAButton>
        </a>
      </div>
    </div>
  </section>
);

// ─── 5. PHOTOGRAPHY SERVICES ─────────────────────────────────────────────
const SERVICES = [
  {
    n: "01",
    title: "Menu Photography",
    slug: "hero-laddu-220",
    desc: "Full-menu image libraries shot in a single on-location day. Built for Uber Eats, Menulog, DoorDash, your website and Google.",
    detail: "From $30 / image · $10 on volume",
  },
  {
    n: "02",
    title: "Food & Beverage",
    slug: "menu-malai-tikka",
    desc: "Hero plates and signature dishes for marketing campaigns, openings, menu launches, and editorial features.",
    detail: "Half-day or full-day on-location",
  },
  {
    n: "03",
    title: "Venue & Interior",
    slug: "hero-banquet-01",
    desc: "Front of house, fit-out and ambience photography. Real service, real tables, no awkward staged-empty rooms.",
    detail: "From $450 · combine with menu shoot",
  },
  {
    n: "04",
    title: "Brand & Campaign",
    slug: "hero-banquet-01",
    desc: "Multi-frame campaigns for new openings, expansions, franchise partners and PR — coordinated with your designer.",
    detail: "Custom — request a quote",
  },
];

const PhotographyServices = () => (
  <section id="services" className="bg-ink text-bone py-24 lg:py-36">
    <div className="mx-auto max-w-[1600px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 mb-14 lg:mb-20">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs uppercase tracking-wider2 font-bold text-ember mb-6">
            What we shoot · Photography services
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            Four kinds of shoot.
            <br />
            <span className="italic font-light text-ember">All on location.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:pb-3">
          <p className="text-bone/70 text-base leading-relaxed max-w-md">
            From a quick menu refresh to a full opening campaign, every shoot is
            done at your venue — your kitchen, your plates, your light. No
            studio drop-offs, no shipping food across town.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-bone/10 border border-bone/10">
        {SERVICES.map((s) => (
          <article key={s.n} className="group relative bg-ink hover:bg-[#161613] transition-colors p-5 sm:p-6 md:p-8 lg:p-10 grid grid-cols-12 gap-4 md:gap-6">
            <div className="col-span-12 md:col-span-4">
              <Photo slug={s.slug} alt={`${s.title} Melbourne`} ratio="4/5" sizes="(min-width: 768px) 20vw, 100vw" />
            </div>
            <div className="col-span-12 md:col-span-8 flex flex-col">
              <span className="font-display italic text-2xl text-ember leading-none">{s.n}</span>
              <h3 className="font-display text-2xl md:text-3xl tracking-editorial leading-tight mt-3">
                {s.title}
              </h3>
              <p className="mt-4 text-sm text-bone/75 leading-relaxed flex-1">
                {s.desc}
              </p>
              <p className="mt-6 text-xs uppercase tracking-wider2 font-bold text-ember">
                {s.detail}
              </p>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

// ─── 6. MELBOURNE HOSPITALITY FOCUS ─────────────────────────────────────────────
const MelbourneFocus = () => (
  <section id="melbourne" className="bg-bone text-ink py-24 lg:py-36">
    <div className="mx-auto max-w-[1600px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16">
        <div className="col-span-12 lg:col-span-5 lg:sticky lg:top-32 self-start">
          <p className="text-xs uppercase tracking-wider2 font-bold text-ember mb-6">
            Melbourne hospitality · Mobile coverage
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)" }}>
            Made for Melbourne
            <br />
            <span className="italic font-light text-ember">restaurants and cafés.</span>
          </h2>
          <p className="mt-8 text-char leading-relaxed max-w-md">
            A mobile restaurant photographer Melbourne hospitality businesses
            actually rely on — kitchens in Footscray, cafés in Carlton, late
            tables in Fitzroy, family restaurants out in Point Cook and
            Tarneit. We come to you. No studio drop-offs, no shipping food.
          </p>
          <p className="mt-6 text-char leading-relaxed max-w-md">
            Strongest networks across Melbourne&apos;s western suburbs — and
            travelling daily across the inner north, south-east and CBD.
          </p>
          <a href="#planner" className="inline-block mt-10">
            <CTAButton variant="secondary" size="md">Check coverage & pricing</CTAButton>
          </a>
        </div>

        <div className="col-span-12 lg:col-span-7">
          <div className="space-y-12">
            {SUBURB_GROUPS.map((g, i) => (
              <ScrollReveal key={g.region} delay={i * 80}>
                <div>
                  <div className="flex items-baseline justify-between mb-5 pb-3 border-b border-ink/15">
                    <h3 className="font-display italic text-xl md:text-2xl tracking-editorial">
                      {g.region}
                    </h3>
                    {g.note && (
                      <span className="text-xs uppercase tracking-wider2 font-bold text-ember hidden sm:inline">
                        {g.note}
                      </span>
                    )}
                  </div>
                  <ul className="flex flex-wrap gap-x-5 gap-y-2">
                    {g.suburbs.map((s) => (
                      <li key={s} className="font-display text-lg md:text-xl tracking-editorial text-char hover:text-ember transition-colors">
                        {s}
                        <span className="text-ember/40 ml-5">·</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ─── 7. DELIVERY APP PHOTOGRAPHY ─────────────────────────────────────────────
const DeliveryAppSection = () => (
  <section id="delivery" className="relative bg-ink text-bone py-24 lg:py-36 overflow-hidden">
    <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
    <div className="relative mx-auto max-w-[1600px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16 items-end">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs uppercase tracking-wider2 font-bold text-ember mb-6">
            Uber Eats photography Melbourne · Delivery apps
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 6vw, 5rem)" }}>
            Photos built for the
            <br />
            <span className="italic font-light text-ember">scroll.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5">
          <p className="text-bone/80 leading-relaxed max-w-md">
            Delivery-app menus are graded by photo. A clear, lit, well-cropped
            hero pulls more taps than three average ones combined. We shoot
            specifically for Uber Eats, DoorDash and Menulog — square crops,
            consistent lighting across dishes, no busy backgrounds.
          </p>
        </div>
      </div>

      <div className="mt-16 grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-7 grid grid-cols-3 gap-3 md:gap-4">
          {["menu-butter-chicken", "menu-malai-tikka", "menu-veg-crispy", "menu-chinese-bhel-03", "hero-laddu-220", "menu-fried-rice"].map((s) => (
            <Photo key={s} slug={s} alt={`Uber Eats photography Melbourne — ${s}`} ratio="1/1" sizes="(min-width: 768px) 16vw, 33vw" />
          ))}
        </div>

        <div className="col-span-12 md:col-span-5 bg-[#161613] p-8 md:p-10">
          <p className="text-xs uppercase tracking-wider2 font-bold text-ember mb-5">
            What you get
          </p>
          <ul className="space-y-5 mb-8">
            {[
              "Square + landscape crops per dish",
              "Consistent white-balance across the menu",
              "Files named to match your menu items",
              "Resized for Uber, DoorDash, Menulog, Google",
              "One free re-edit per dish, no questions",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 text-bone/85 text-sm leading-relaxed">
                <Check size={14} strokeWidth={1.6} className="text-ember mt-1 shrink-0" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
          <a href="#enquiry">
            <CTAButton variant="ghost" size="md">Audit my delivery menu</CTAButton>
          </a>
        </div>
      </div>
    </div>
  </section>
);

// ─── 8. ENQUIRY CTA ─────────────────────────────────────────────
const EnquiryCTA = () => (
  <section id="enquiry" className="bg-bone text-ink py-24 lg:py-36">
    <div className="mx-auto max-w-[1600px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-10 lg:gap-16">
        <div className="col-span-12 lg:col-span-5">
          <p className="text-xs uppercase tracking-wider2 font-bold text-ember mb-6">
            Direct enquiry · Hand-confirmed
          </p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5vw, 4.5rem)" }}>
            Tell us about
            <br />
            <span className="italic font-light text-ember">your shoot.</span>
          </h2>
          <p className="mt-8 text-char leading-relaxed max-w-md">
            Quick details below or call directly — most quotes are confirmed
            inside one business day with no marketing chase, no spam, and no
            forwarded sales calls.
          </p>

          <div className="mt-10 space-y-5 border-t border-ink/15 pt-8">
            <a href="tel:0494756111" data-testid="enquiry-phone" className="group flex items-center gap-4">
              <Phone size={16} strokeWidth={1.4} className="text-ember" />
              <div>
                <p className="text-xs uppercase tracking-wider2 font-bold text-char-mute mb-1">Direct</p>
                <p className="font-sans font-bold text-2xl tracking-tight group-hover:text-ember transition-colors">
                  0494 756 111
                </p>
              </div>
            </a>
            <a href="mailto:hello@restaurantphotographer.com.au" data-testid="enquiry-email" className="group flex items-center gap-4">
              <Mail size={16} strokeWidth={1.4} className="text-ember" />
              <div>
                <p className="text-xs uppercase tracking-wider2 font-bold text-char-mute mb-1">Email</p>
                <p className="font-display text-lg md:text-xl tracking-editorial group-hover:text-ember transition-colors break-all">
                  hello@restaurantphotographer.com.au
                </p>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <MapPin size={16} strokeWidth={1.4} className="text-ember" />
              <div>
                <p className="text-xs uppercase tracking-wider2 font-bold text-char-mute mb-1">Coverage</p>
                <p className="font-display text-lg tracking-editorial">
                  Mobile · Servicing all Melbourne suburbs
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="col-span-12 lg:col-span-7">
          <QuoteForm />
        </div>
      </div>
    </div>
  </section>
);
